import React from 'react';

const kpiHideBelowMd = 'hidden md:flex md:flex-col';

function getWinRateTone(pct) {
  if (pct == null || Number.isNaN(pct)) {
    return {
      value: 'text-text-primary',
      card: 'bg-surface border-border-medium',
    };
  }
  if (pct > 50) {
    return {
      value: 'text-success-dark',
      card: 'bg-success-light/80 border-success/40 ring-1 ring-success/20',
    };
  }
  if (pct < 50) {
    return {
      value: 'text-danger-dark',
      card: 'bg-danger-light/80 border-danger/40 ring-1 ring-danger/20',
    };
  }
  return {
    value: 'text-text-primary',
    card: 'bg-surface border-border-medium',
  };
}

function MatchupStatCard({ label, value, valueClassName = '', className = '', cardClassName = '' }) {
  return (
    <div
      className={`flex flex-col justify-between min-h-[4.25rem] sm:min-h-[4.75rem] rounded-lg border px-2.5 py-2.5 sm:px-3 sm:py-3 min-w-0 bg-surface border-border-medium shadow-sm ${cardClassName} ${className}`}
    >
      <p className="text-[10px] sm:text-[11px] uppercase tracking-wider text-text-muted font-semibold leading-tight line-clamp-2">
        {label}
      </p>
      <p className={`mt-1.5 text-xl sm:text-2xl font-bold tabular-nums leading-none ${valueClassName}`}>
        {value}
      </p>
    </div>
  );
}

function ResponsiveLossesLabel({ shortLabel, longLabel }) {
  return (
    <>
      <span className="md:hidden">{shortLabel}</span>
      <span className="hidden md:inline">{longLabel}</span>
    </>
  );
}

/**
 * Matchup summary stats — light cards; use embedded inside MatchupPlayerPicker.
 *
 * Order: Win rate (first, green/red) → Wins → Their losses → Total games (md+).
 */
export function MatchupStatsHero({
  variant = 'individual',
  wins = 0,
  losses = 0,
  totalGames = 0,
  winRate,
  opponentLabel,
  embedded = false,
}) {
  const configs = {
    individual: {
      winsLabel: 'Your wins',
      lossesShort: 'Theirs',
      lossesLong: opponentLabel ? `${opponentLabel}'s wins` : 'Their wins',
      winRateLabel: 'Win rate',
    },
    teammate: {
      winsLabel: 'Wins together',
      lossesShort: 'Losses',
      lossesLong: 'Losses together',
      winRateLabel: 'Win rate',
    },
    team: {
      winsLabel: 'Your team wins',
      lossesShort: 'Them',
      lossesLong: 'Opponent wins',
      winRateLabel: 'Team win rate',
    },
  };

  const cfg = configs[variant] ?? configs.individual;
  const games = totalGames > 0 ? totalGames : wins + losses;
  const pct = winRate != null ? Number(winRate) : null;
  const winRateDisplay = pct != null ? `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%` : '—';
  const winRateTone = getWinRateTone(pct);

  const grid = (
    <div className="grid grid-cols-3 md:grid-cols-4 gap-2 sm:gap-2.5 items-stretch">
      <MatchupStatCard
        label={cfg.winRateLabel}
        value={winRateDisplay}
        valueClassName={winRateTone.value}
        cardClassName={winRateTone.card}
      />
      <MatchupStatCard label={cfg.winsLabel} value={wins} valueClassName="text-info-dark" />
      <MatchupStatCard
        label={<ResponsiveLossesLabel shortLabel={cfg.lossesShort} longLabel={cfg.lossesLong} />}
        value={losses}
        valueClassName="text-danger-dark"
      />
      <MatchupStatCard
        className={kpiHideBelowMd}
        label="Total games"
        value={games > 0 ? games : '—'}
        valueClassName="text-text-primary"
      />
    </div>
  );

  if (embedded) {
    return grid;
  }

  return (
    <section
      className="rounded-xl border border-border-light bg-surface-subtle/50 p-2 sm:p-3"
      aria-label="Matchup statistics"
    >
      {grid}
    </section>
  );
}

export default MatchupStatsHero;
